import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowRightLeft, Loader2, MessageSquareText } from 'lucide-react';
import { CounterOfferThread } from '@/components/marketplace/CounterOfferThread';
import { CounterOfferModal } from '@/components/marketplace/CounterOfferModal';
import { useCounterOffers } from '@/hooks/useCounterOffers';
import { useDealApplications } from '@/hooks/useDealApplications';

/**
 * Brand-side negotiations surface — one card per talent application,
 * each rendering the shared CounterOfferThread (read + accept/reject)
 * and a "Propose Terms" action that opens the shared CounterOfferModal.
 *
 * Symmetric to the talent-side surface in MarketplaceDealDetailModal,
 * but scoped to a brand viewing all applications on a single deal.
 *
 * Each per-application card owns its own useCounterOffers instance so
 * state stays scoped — accepting a counter on one application doesn't
 * disrupt the threads on the others.
 */
interface BrandDealNegotiationsTabProps {
  dealId: string;
  dealValue: number;
  dealType: string;
  estimatedCompletionDate?: string;
  onActivated?: () => void;
}

interface ApplicationCardProps {
  applicationId: string;
  applicationStatus: string;
  talentName: string;
  talentPhoto: string | null;
  dealId: string;
  currentValue: number;
  currentDealType: string;
  currentDeadline?: string;
  onActivated?: () => void;
}

const ApplicationNegotiationCard: React.FC<ApplicationCardProps> = ({
  applicationId,
  applicationStatus,
  talentName,
  talentPhoto,
  dealId,
  currentValue,
  currentDealType,
  currentDeadline,
  onActivated,
}) => {
  const {
    counterOffers,
    loading,
    fetchCounterOffers,
    respondToCounterOffer,
  } = useCounterOffers();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchCounterOffers(applicationId);
  }, [applicationId, fetchCounterOffers]);

  const initials = (talentName || '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const pendingCount = counterOffers.filter((o) => o.status === 'pending').length;

  const handleAccept = async (counterId: string) => {
    const result = await respondToCounterOffer(counterId, 'accepted', applicationId);
    if (result.success) {
      // The deal is now active at the new value — let the parent refresh.
      onActivated?.();
      // Re-fetch to flip the local thread status
      fetchCounterOffers(applicationId);
    }
  };

  const handleReject = async (counterId: string) => {
    await respondToCounterOffer(counterId, 'rejected', applicationId);
    fetchCounterOffers(applicationId);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              {talentPhoto && <AvatarImage src={talentPhoto} alt={talentName} />}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-base">{talentName}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs capitalize">
                  Application: {applicationStatus}
                </Badge>
                {pendingCount > 0 && (
                  <Badge variant="default" className="text-xs">
                    {pendingCount} pending offer{pendingCount > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2"
          >
            <ArrowRightLeft className="h-4 w-4" />
            Propose Terms
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading offers…
          </div>
        ) : (
          <CounterOfferThread
            counterOffers={counterOffers}
            onAccept={handleAccept}
            onReject={handleReject}
            onCounter={() => setModalOpen(true)}
            loading={loading}
          />
        )}
      </CardContent>

      <CounterOfferModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        dealApplicationId={applicationId}
        dealId={dealId}
        currentValue={currentValue}
        currentDealType={currentDealType}
        currentDeadline={currentDeadline}
        onSuccess={() => {
          fetchCounterOffers(applicationId);
          onActivated?.();
        }}
      />
    </Card>
  );
};

export const BrandDealNegotiationsTab: React.FC<BrandDealNegotiationsTabProps> = ({
  dealId,
  dealValue,
  dealType,
  estimatedCompletionDate,
  onActivated,
}) => {
  const { applications, loading, fetchApplicationsForDeal } = useDealApplications(dealId);

  useEffect(() => {
    fetchApplicationsForDeal(dealId);
    // fetchApplicationsForDeal is a stable function created in the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Show only applications that haven't been hard-rejected; once a deal
  // is awarded other apps flip to rejected — those don't need negotiation.
  const negotiable = useMemo(
    () => applications.filter((a) => a.status !== 'rejected'),
    [applications],
  );

  if (loading && applications.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading negotiations…
        </CardContent>
      </Card>
    );
  }

  if (negotiable.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center justify-center gap-3 text-center">
          <MessageSquareText className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground max-w-sm">
            No applications to negotiate yet. Once a talent applies, you can
            counter their proposed terms or accept the deal as posted.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {negotiable.map((app) => (
        <ApplicationNegotiationCard
          key={app.id}
          applicationId={app.id}
          applicationStatus={app.status}
          talentName={app.talent_name}
          talentPhoto={app.talent_photo}
          dealId={dealId}
          currentValue={dealValue}
          currentDealType={dealType}
          currentDeadline={estimatedCompletionDate}
          onActivated={onActivated}
        />
      ))}
    </div>
  );
};
